import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';
import { useTheme } from '../../context/ThemeContext';

import { auth } from '../../firebase';

export default function Home() {
    // 🌍 Global Theme
    const { isDarkMode, toggleTheme } = useTheme();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstallable, setIsInstallable] = useState(false);

    // Handle FCM Permission on Mount
    useEffect(() => {
        // Notification logic removed
    }, []);

    const themeText = isDarkMode ? 'text-white' : 'text-slate-900';
    const themeSubText = isDarkMode ? 'text-slate-300' : 'text-slate-600';
    const themeCardBg = isDarkMode ? 'bg-white/5 border border-white/10 backdrop-blur-xl shadow-2xl hover:bg-white/10' : 'bg-white border border-slate-100 shadow-xl shadow-blue-500/5 hover:border-blue-100';
    const themeNav = isDarkMode ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300' : 'text-slate-900';

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later.
            setDeferredPrompt(e);
            setIsInstallable(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            // Silently return or log debug info, avoiding popup spam
            // Install prompt not available

            return;
        }

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        // User response to install prompt handled


        // We've used the prompt, and can't use it again, throw it away
        setDeferredPrompt(null);
        setIsInstallable(false);
    };

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-midnight' : 'bg-daylight'} transition-all duration-500 overflow-x-hidden overflow-y-auto scroll-smooth relative font-sans selection:bg-purple-500 selection:text-white`}>
            <SEO
                title="ChatIQ - Secure, Private & Modern Messaging"
                description="ChatIQ is the ultimate secure messaging app featuring end-to-end encryption, high-quality voice/video calls, and a stunning modern interface. No ads, no tracking."
                keywords={['Secure Chat', 'Private Messaging', 'Encrypted Calls', 'Video Chat', 'Group Chat', 'ChatIQ', 'Web Messenger', 'PWA']}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "SoftwareApplication",
                    "name": "ChatIQ",
                    "operatingSystem": "Web, Android",
                    "applicationCategory": "CommunicationApplication",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "USD"
                    },
                    "description": "ChatIQ is a secure, modern messaging application with end-to-end encryption.",
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": "4.8",
                        "ratingCount": "1250"
                    }
                }}
            />


            {/* Background Gradients & Blobs */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className={`absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full blur-[80px] animate-blob mix-blend-screen transition-all duration-700 ${isDarkMode ? 'bg-purple-600 opacity-25' : 'bg-purple-600 opacity-40'} `}></div>
                <div className={`absolute top-[20%] right-[-10%] w-[600px] h-[600px] rounded-full blur-[80px] animate-blob animation-delay-2000 mix-blend-screen transition-all duration-700 ${isDarkMode ? 'bg-indigo-600 opacity-20' : 'bg-blue-600 opacity-30'} `}></div>
                <div className={`absolute bottom-[-20%] left-[20%] w-[600px] h-[600px] rounded-full blur-[80px] animate-blob animation-delay-4000 mix-blend-screen transition-all duration-700 ${isDarkMode ? 'bg-cyan-600 opacity-20' : 'bg-pink-600 opacity-30'} `}></div>
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150"></div>
            </div>

            {/* Navbar */}
            <nav className="relative z-50 flex items-center justify-between px-6 py-6 max-w-7xl mx-auto">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                    </div>
                    <span className={`text-xl font-bold tracking-tight transition-colors ${themeNav} `}>ChatIQ</span>
                </div>
                <div className="flex items-center gap-4">
                    <Link to="/login" className={`hidden md:block px-5 py-2 rounded-full text-sm font-medium transition-all ${isDarkMode ? 'text-slate-200 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-slate-900 hover:bg-black/5'} `}>
                        Log In
                    </Link>
                    <Link to="/login" className="px-5 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold shadow-lg shadow-blue-500/30 transition-all hover:scale-105">
                        Get Started
                    </Link>

                    {/* Theme Toggle Button - Moved to Right */}
                    <button
                        onClick={toggleTheme}
                        className={`p-2 rounded-full transition-all duration-300 ml-2 ${isDarkMode ? 'bg-white/10 text-yellow-400 hover:bg-white/20' : 'bg-slate-200 text-slate-700 hover:bg-slate-300 shadow-md'} `}
                        aria-label="Toggle Theme"
                    >
                        {isDarkMode ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                        )}
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="relative z-10 flex flex-col items-center justify-center pt-24 pb-20 px-4 text-center max-w-5xl mx-auto">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md mb-8 animate-fade-in-up border transition-all ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white/60 border-white/20 shadow-sm'} `}>
                    <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.5)] animate-pulse"></span>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-600'} `}>v3.7 Secure & Real-time</span>
                </div>

                <h1 className={`text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[1.1] mb-8 bg-clip-text text-transparent bg-gradient-to-br from-white via-white to-blue-200 drop-shadow-2xl animate-fade-in-up animation-delay-100 ${isDarkMode ? '' : 'text-slate-900 bg-none'} `}>
                    {isDarkMode ? (
                        <>Connect beyond <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">boundaries.</span></>
                    ) : (
                        <span className="text-slate-900 drop-shadow-none">Connect beyond <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">boundaries.</span></span>
                    )}
                </h1>

                <p className={`text-lg max-w-2xl mb-12 leading-relaxed animate-fade-in-up animation-delay-200 px-4 transition-colors ${themeSubText} `}>
                    Experience the next generation of messaging. Crystal clear calls,
                    end-to-end encryption, and a beautiful interface designed for you.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto animate-fade-in-up animation-delay-300 justify-center">
                    <Link to="/login" className="px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg shadow-[0_0_40px_rgba(79,70,229,0.4)] hover:shadow-[0_0_60px_rgba(79,70,229,0.6)] hover:scale-105 transition-all w-full sm:w-auto">
                        Start Chatting Now
                    </Link>

                    {/* APK Download Button */}
                    <a
                        href="/chatiq.apk"
                        download="ChatIQ.apk"
                        className={`group px-8 py-4 rounded-full border text-lg font-bold transition-all flex items-center justify-center gap-2 w-full sm:w-auto ${isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-600 text-white hover:bg-slate-750' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-800 hover:bg-slate-50 shadow-sm'} `}
                    >
                        <svg className={`w-5 h-5 transition-colors ${isDarkMode ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-gray-900'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download App
                    </a>
                </div>
            </main>

            {/* Why Choose Us Section */}
            <section className={`relative z-10 max-w-7xl mx-auto px-6 py-20 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-200'} `}>
                <div className="text-center mb-16">
                    <h2 className={`text-3xl md:text-5xl font-bold mb-6 ${themeText} `}>Why ChatIQ?</h2>
                    <p className={`max-w-2xl mx-auto text-lg ${themeSubText} `}>
                        We've built a platform that puts your privacy and experience first.
                        No ads, no tracking, just pure connection.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <FeatureCard
                        title="Secure Messaging"
                        desc="Advanced Double Ratchet algorithms ensure your messages are secure. Only you and the recipient can read them."
                        icon={<LockIcon />}
                        delay="0"
                        bgClass={themeCardBg}
                        textClass={themeText}
                        subTextClass={themeSubText}
                    />
                    <FeatureCard
                        title="Audio Calls"
                        desc="Crystal clear voice calling. Connect with friends and family instantly, no matter where they are."
                        icon={<PhoneIcon />}
                        delay="100"
                        bgClass={themeCardBg}
                        textClass={themeText}
                        subTextClass={themeSubText}
                    />
                    <FeatureCard
                        title="Video Calls"
                        desc="High-definition video calls. See every smile and expression as if you were in the same room."
                        icon={<VideoIcon />}
                        delay="200"
                        bgClass={themeCardBg}
                        textClass={themeText}
                        subTextClass={themeSubText}
                    />
                    <FeatureCard
                        title="Seen Status"
                        desc="Know exactly when your messages are delivered and read with intuitive checkmark indicators."
                        icon={<CheckIcon />}
                        delay="300"
                        bgClass={themeCardBg}
                        textClass={themeText}
                        subTextClass={themeSubText}
                    />
                    <FeatureCard
                        title="Online Presence"
                        desc="See who is currently active and ready to chat with real-time online status indicators."
                        icon={<OnlineIcon />}
                        delay="400"
                        bgClass={themeCardBg}
                        textClass={themeText}
                        subTextClass={themeSubText}
                    />
                    <FeatureCard
                        title="Typing Indicators"
                        desc="Real-time typing feedback makes conversations feel fluid and natural, just like talking in person."
                        icon={<TypingIcon />}
                        delay="500"
                        bgClass={themeCardBg}
                        textClass={themeText}
                        subTextClass={themeSubText}
                    />
                </div>
            </section>

            {/* Security Section */}
            <section className={`relative z-10 py-24 mt-12 overflow-hidden ${isDarkMode ? 'bg-slate-900/80' : 'bg-gradient-to-b from-white to-blue-50'} `}>
                {/* Decorative background elements */}
                <div className={`absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none ${isDarkMode ? 'opacity-20' : 'opacity-40'} `}>
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500 rounded-full blur-[120px] opacity-20 -translate-y-1/2 translate-x-1/3"></div>
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500 rounded-full blur-[120px] opacity-20 translate-y-1/3 -translate-x-1/3"></div>
                </div>

                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-16 relative z-10">
                    <div className="flex-1 text-center md:text-left">
                        <h2 className={`text-3xl md:text-5xl font-bold mb-6 leading-tight ${themeText} `}>
                            Security that doesn't <br />
                            <span className="text-blue-500">compromise speed.</span>
                        </h2>
                        <p className={`text-lg mb-8 leading-relaxed ${themeSubText} `}>
                            We use state-of-the-art Double Ratchet algorithms to ensure your messages stay private.
                            Keys are generated on your device and never leave it.
                            Experience the freedom of truly private communication.
                        </p>
                        <Link to="/privacy-policy" className="inline-flex items-center text-blue-500 hover:text-blue-600 font-semibold group text-lg">
                            Read our Security Whitepaper
                            <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </Link>
                    </div>

                    <div className="flex-1 relative w-full">
                        <div className="absolute inset-0 bg-blue-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
                        <div className={`relative border rounded-2xl p-8 backdrop-blur-xl transform rotate-1 hover:rotate-0 transition-all duration-500 shadow-2xl ${isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-white/50 ring-1 ring-slate-100'} `}>
                            <div className="flex items-center gap-4 mb-8">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'} `}>
                                    <LockIcon />
                                </div>
                                <div>
                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} `}>Current Session</div>
                                    <div className={`font-mono text-xs tracking-wider ${isDarkMode ? 'text-green-400' : 'text-green-600'} `}>VERIFIED • E2EE ENABLED</div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className={`h-3 rounded-full w-3/4 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} `}></div>
                                <div className={`h-3 rounded-full w-1/2 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} `}></div>
                                <div className={`h-3 rounded-full w-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} `}></div>
                            </div>

                            {/* Floating badge */}
                            <div className={`absolute -bottom-6 -right-6 px-6 py-3 rounded-xl shadow-lg border backdrop-blur-md flex items-center gap-2 animate-bounce-slow ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-white text-slate-800'} `}>
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                </span>
                                <span className="font-bold text-sm">Encrypted</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className={`relative z-10 border-t py-12 ${isDarkMode ? 'border-white/5 bg-black/20' : 'border-slate-200 bg-slate-50'} `}>
                <div className={`max-w-7xl mx-auto px-6 text-center text-sm ${themeSubText} `}>
                    <p className="mb-4">&copy; {new Date().getFullYear()} ChatIQ. Crafted with ❤️ for the world.</p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 font-medium">
                        <Link to="/privacy-policy" className={`transition-colors ${isDarkMode ? 'hover:text-white' : 'hover:text-slate-900'} `}>Privacy Policy</Link>
                        <Link to="/terms-of-service" className={`transition-colors ${isDarkMode ? 'hover:text-white' : 'hover:text-slate-900'} `}>Terms and Conditions</Link>
                        <Link to="/support" className={`transition-colors ${isDarkMode ? 'hover:text-white' : 'hover:text-slate-900'} `}>Customer Support</Link>
                    </div>
                </div>
            </footer>
        </div >
    );
}

// Subcomponents for cleaner code
function FeatureCard({ title, desc, icon, delay, bgClass, textClass, subTextClass }) {
    return (
        <div className={`p-8 rounded-3xl transition-all duration-300 hover:scale-[1.02] group animate-fade-in-up hover:shadow-xl ${bgClass} `} style={{ animationDelay: `${delay} ms` }}>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 border border-white/5">
                <div className="text-blue-500 group-hover:text-blue-400 transition-colors">
                    {icon}
                </div>
            </div>
            <h3 className={`text-xl font-bold mb-3 ${textClass} `}>{title}</h3>
            <p className={`leading-relaxed text-base ${subTextClass} `}>{desc}</p>
        </div>
    );
}

// Icons
const LockIcon = () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

const PhoneIcon = () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
);

const VideoIcon = () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
);

const CheckIcon = () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
);

const OnlineIcon = () => (
    <div className="w-4 h-4 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.6)]"></div>
);

const TypingIcon = () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
);
